import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tags, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DescriptionSummary } from "./DescriptionSummary";
import { StepFooter } from "./StepFooter";

interface KeywordsStepProps {
  description: string;
  keywords: string[];
  onKeywordsChange: (val: string[]) => void;
  excludeKeywords: string[];
  onExcludeKeywordsChange: (val: string[]) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkipToReview: () => void;
}

export function KeywordsStep({
  description,
  keywords,
  onKeywordsChange,
  excludeKeywords,
  onExcludeKeywordsChange,
  onBack,
  onContinue,
  onSkipToReview,
}: KeywordsStepProps) {
  const { t } = useTranslation("search");
  const [includeInput, setIncludeInput] = useState("");
  const [excludeInput, setExcludeInput] = useState("");

  const handleIncludeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const trimmed = includeInput.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      onKeywordsChange([...keywords, trimmed]);
    }
    setIncludeInput("");
  };

  const handleExcludeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const trimmed = excludeInput.trim();
    if (trimmed && !excludeKeywords.includes(trimmed)) {
      onExcludeKeywordsChange([...excludeKeywords, trimmed]);
    }
    setExcludeInput("");
  };

  const removeKeyword = (keyword: string) => {
    onKeywordsChange(keywords.filter((k) => k !== keyword));
  };

  const removeExcludeKeyword = (keyword: string) => {
    onExcludeKeywordsChange(excludeKeywords.filter((k) => k !== keyword));
  };

  return (
    <div className="space-y-4">
      <DescriptionSummary description={description} />

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Tags className="h-4 w-4 text-primary" />
            {t("wizard.keywordsStep.cardTitle")}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Include Keywords */}
          <div className="space-y-2">
            <label className="flex items-center text-xs font-medium text-foreground">
              <span className="inline-block w-2 h-2 rounded-full bg-primary mr-1" />
              {t("wizard.keywordsStep.includeLabel")}
            </label>
            <Input
              type="text"
              placeholder={t("wizard.keywordsStep.includePlaceholder")}
              value={includeInput}
              onChange={(e) => setIncludeInput(e.target.value)}
              onKeyDown={handleIncludeKeyDown}
            />
            {keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {keywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-primary bg-primary/8 text-primary"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => removeKeyword(keyword)}
                      className="text-primary/60 hover:text-primary"
                      aria-label={t("wizard.keywordsStep.removeKeyword", {
                        keyword,
                      })}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t("wizard.keywordsStep.includeHint")}
            </p>
          </div>

          {/* Exclude Keywords */}
          <div className="border-t border-border pt-4 space-y-2">
            <label className="flex items-center text-xs font-medium text-foreground">
              <span className="inline-block w-2 h-2 rounded-full bg-destructive mr-1" />
              {t("wizard.keywordsStep.excludeLabel")}
            </label>
            <Input
              type="text"
              placeholder={t("wizard.keywordsStep.excludePlaceholder")}
              value={excludeInput}
              onChange={(e) => setExcludeInput(e.target.value)}
              onKeyDown={handleExcludeKeyDown}
            />
            {excludeKeywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {excludeKeywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-destructive bg-destructive/8 text-destructive"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => removeExcludeKeyword(keyword)}
                      className="text-destructive/60 hover:text-destructive"
                      aria-label={t(
                        "wizard.keywordsStep.removeExcludeKeyword",
                        {
                          keyword,
                        },
                      )}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t("wizard.keywordsStep.excludeHint")}
            </p>
          </div>
        </CardContent>
      </Card>

      <StepFooter
        onBack={onBack}
        onContinue={onContinue}
        onSkipToReview={onSkipToReview}
      />
    </div>
  );
}
